import React, { useEffect, useState } from 'react';
import { MessageSquare, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface HeroSectionProps {
  onChatOpen: () => void;
}

interface ChatMessage {
  id: number;
  content: string;
  sender: 'user' | 'assistant';
  type: 'text' | 'list';
  items?: string[];
}

const chatSequence: ChatMessage[] = [
  {
    id: 1,
    content: "Welke AI-oplossingen bieden jullie aan?",
    sender: 'user',
    type: 'text'
  },
  {
    id: 2,
    content: "Wij bieden geavanceerde AI-chatoplossingen met de volgende functies:",
    sender: 'assistant',
    type: 'list',
    items: [
      'Geavanceerde natuurlijke taalverwerking',
      '24/7 beschikbare directe antwoorden',
      'Ondersteuning voor meerdere talen'
    ]
  },
  {
    id: 3,
    content: "Hoe nauwkeurig begrijpt jullie AI klantvragen?",
    sender: 'user',
    type: 'text'
  },
  {
    id: 4,
    content: "Onze AI behaalt een nauwkeurigheid van meer dan 95% bij het begrijpen en beantwoorden van klantvragen, dankzij geavanceerde natuurlijke taalverwerking.",
    sender: 'assistant',
    type: 'text'
  },
  {
    id: 5,
    content: "Werkt het systeem met meerdere talen?",
    sender: 'user',
    type: 'text'
  },
  {
    id: 6,
    content: "Ja! Ons systeem ondersteunt meerdere talen en detecteert automatisch de voorkeurstaal van de gebruiker om daarin te antwoorden.",
    sender: 'assistant',
    type: 'text'
  }
];

export function HeroSection({ onChatOpen }: HeroSectionProps) {
  const [visibleMessages, setVisibleMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    let currentIndex = 0;
    let isActive = true;
    
    const showNextMessage = () => {
      if (!isActive) return;
      
      setIsTyping(true);
      
      setTimeout(() => {
        if (!isActive) return;
        
        setIsTyping(false);
        setVisibleMessages(prev => {
          const newMessages = [...prev, chatSequence[currentIndex]];
          return newMessages.slice(Math.max(0, newMessages.length - 4));
        });
        
        currentIndex = (currentIndex + 1) % chatSequence.length;
        
        setTimeout(showNextMessage, 1000);
      }, 1500);
    };

    setTimeout(showNextMessage, 500);

    return () => {
      isActive = false;
      setVisibleMessages([]);
    };
  }, []);

  return (
    <header className="bg-gradient-to-r from-brand-600 to-brand-500 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-1 space-y-8"
          >
            <h1 className="text-5xl font-bold leading-tight">
              De Toekomst van Klantenservice met AI
            </h1>
            <p className="text-xl text-brand-50">
              Onze intelligente AI-chatassistent biedt direct accurate antwoorden, 24 uur per dag. Verbeter uw klantenservice met geavanceerde gesprekstechnologie.
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={onChatOpen}
                className="bg-white text-brand-600 px-8 py-3 rounded-lg font-semibold hover:bg-brand-50 transition-colors flex items-center gap-2"
              >
                Start de AI Chat <MessageSquare size={20} />
              </button>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex-1"
          >
            <div className="bg-[#f0f2f5] rounded-2xl shadow-2xl overflow-hidden max-w-md mx-auto">
              <div className="bg-[#1e2321] p-4 flex items-center gap-3">
                <div className="relative">
                  <img
                    src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150"
                    alt="AI Assistent"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-[#1e2321]"></div>
                </div>
                <div>
                  <h3 className="font-semibold text-white">Julia</h3>
                  <p className="text-xs text-gray-300">AI Klantenondersteuning</p>
                </div>
              </div>

              <div className="bg-[#efeae2] h-[300px] overflow-y-auto">
                <div className="p-4 space-y-4">
                  <AnimatePresence mode="popLayout">
                    {visibleMessages.map((message) => (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`${
                            message.sender === 'user' 
                              ? 'bg-[#d9fdd3]' 
                              : 'bg-white'
                          } text-gray-800 rounded-lg p-3 max-w-[80%] relative shadow-sm`}
                        >
                          <p>{message.content}</p>
                          {message.items && (
                            <ul className="mt-2 space-y-1">
                              {message.items.map((item, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <span className="text-brand-500">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <span className="text-[10px] text-gray-500">
                              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {message.sender === 'user' && (
                              <>
                                <Check className="w-4 h-4 text-[#53bdeb]" />
                                <Check className="w-4 h-4 text-[#53bdeb] -ml-3" />
                              </>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}

                    {isTyping && (
                      <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="flex justify-start"
                      >
                        <div className="bg-white rounded-lg p-4 flex items-center gap-1">
                          <motion.div 
                            className="w-2 h-2 bg-gray-400 rounded-full"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          />
                          <motion.div 
                            className="w-2 h-2 bg-gray-400 rounded-full"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                          />
                          <motion.div 
                            className="w-2 h-2 bg-gray-400 rounded-full"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="bg-[#f0f2f5] p-4 flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Type een bericht"
                  className="flex-1 bg-white rounded-full px-4 py-2 focus:outline-none"
                  readOnly
                />
                <button className="bg-brand-500 text-white p-2 rounded-full hover:bg-brand-600 transition-colors">
                  <MessageSquare size={20} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </header>
  );
}